import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined';
import MicNoneRoundedIcon from '@material-ui/icons/MicNoneRounded';
import PhotoLibraryOutlinedIcon from '@material-ui/icons/PhotoLibraryOutlined';
import SendRoundedIcon from '@material-ui/icons/SendRounded';
import StopRoundedIcon from '@material-ui/icons/StopRounded';
import { Form as FormikForm } from 'formik';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  media,
  neutral,
  radius,
  shadow,
  space,
  transition,
} from '../../../theme/tokens';
import * as common from './Form';

// shared look for the camera / mic / send icon buttons
const iconButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  padding: 0;
  border: none;
  border-radius: ${radius.pill};
  background: none;
  transition: background ${transition.fast}, color ${transition.fast};
  &:hover {
    cursor: pointer;
    background: ${neutral.subtle};
  }
  ${focusRing}
`;

export const Form = styled(FormikForm)`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  grid-template-rows: auto min-content;
  grid-template-areas:
    'camera text mic send'
    '. error error .';
  align-items: center;
  justify-items: center;
  column-gap: ${space[1]};
  min-height: 8rem;
  width: 100%;
  margin: 0;
  padding: ${space[3]} ${space[2]};
  position: relative;
  box-shadow: ${shadow.up};
  background: ${({ theme }) => theme.appBackground};

  ${media.md`
    column-gap: ${space[2]};
    padding: ${space[3]} ${space[4]};
  `}
`;

export const MessageSubmitButton = styled.button`
  grid-area: send;
  ${iconButton}
  color: ${({ theme }) => theme.userMessage};
  &:disabled {
    color: ${({ theme }) => theme.textDark};
    &:hover {
      cursor: default;
      background: none;
    }
  }
`;

export const MessageInput = styled(common.FieldInput)`
  grid-area: text;
  width: 100%;
  min-width: 0;
  font-size: ${fontSize.md};
`;

export const CameraLabel = styled.button`
  grid-area: camera;
  ${iconButton}
  color: ${({ theme }) => theme.textDark};
  ${({ fileSelected }) =>
    fileSelected &&
    css`
      color: ${({ theme }) => theme.userMessage};
    `}
`;

export const AttachMenuContainer = styled.section`
  display: flex;
  flex-direction: column;
  padding: ${space[2]} 0;
`;

export const AttachMenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  width: 100%;
  min-height: ${layout.touchTarget};
  border: none;
  background: none;
  padding: ${space[3]} ${space[4]};
  font-family: inherit;
  font-size: ${fontSize.md};
  text-align: left;
  color: ${({ theme }) => theme.textDark};
  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.hoverFill};
  }
  ${focusRing}
`;
export const RecordButton = styled.button`
  grid-area: mic;
  ${iconButton}
  color: ${({ theme }) => theme.textDark};
  ${({ fileSelected }) =>
    fileSelected &&
    css`
      color: ${({ theme }) => theme.userMessage};
    `}
  ${({ isRecording }) =>
    isRecording &&
    css`
      color: ${({ theme }) => theme.buttonWarning};
    `}
`;

export const HiddenImageInput = styled.input`
  display: none;
`;

export const CameraIcon = () => (
  <CameraAltOutlinedIcon style={{ fontSize: '2.8rem' }} />
);

export const LibraryIcon = () => (
  <PhotoLibraryOutlinedIcon style={{ fontSize: '2.2rem' }} />
);

export const MicIcon = () => (
  <MicNoneRoundedIcon style={{ fontSize: '2.8rem' }} />
);

export const StopIcon = () => (
  <StopRoundedIcon style={{ fontSize: '2.8rem' }} />
);

export const SendIcon = () => (
  <SendRoundedIcon style={{ fontSize: '3.2rem' }} />
);

export const ValidationMessage = styled.p`
  grid-area: error;
  margin: ${space[1]} 0 0;
  font-size: ${fontSize.sm};
  color: ${({ theme }) => theme.errorMessage};
`;

export const EvidencePreview = styled.div`
  margin-top: ${space[4]};
  padding-bottom: ${space[2]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  background: ${({ theme }) => theme.appBackground};
  overflow-wrap: anywhere;
`;

export const EvidencePreviewHeadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const EvidencePreviewHeading = styled.h2`
  margin: ${space[4]} ${space[3]} ${space[2]};
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.bold};
  line-height: ${lineHeight.tight};
  text-align: center;
  color: ${({ theme }) => theme.subHeaderColour};
`;

export const CommentPreview = styled.div`
  width: 100%;
  padding-bottom: ${space[2]};
  border: 1px solid ${neutral.border};
  border-bottom: none;
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  background: ${({ theme }) => theme.appBackground};
  overflow-wrap: anywhere;
`;

export const MessageContainer = styled.div`
  width: fit-content;
  max-width: 100%;
  margin-left: auto;
  margin-right: ${space[2]};
  ${media.md`
    margin-right: ${space[7]};
  `}
`;

export * from '../../common/elements/Form';
